/**
 * prepareOffline — warm the offline cache with everything a rep needs in the
 * field: products, categories, customers, orders, returns, collections,
 * outstanding, gifts and visits. Each api.get below is auto-cached by the API
 * client; combined with the path-level offline fallback, the screens will read
 * these even if their own params differ slightly.
 *
 * Pass an `onProgress(label)` callback to drive a progress UI.
 */
import { api, getBackend } from '@/api/apiClient';

const BIG = { limit: 1000, page: 0 };
const LIST = { limit: 500, page: 0 };

// Try a primary endpoint, then optional legacy fallbacks, returning the first
// that resolves. Each is also auto-cached on success.
async function firstOf(calls) {
  let lastErr;
  for (const call of calls) {
    try { return await call(); } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('no endpoint');
}

const STEPS = [
  ['المنتجات', () => firstOf([
    () => api.get('/sellgo_inventory', BIG),
    () => api.get('/sellgo-products', BIG),
    () => api.get('/products', BIG),
  ])],
  ['الأصناف', () => api.get('/products/categories')],
  ['العملاء', () => api.get('/customers', BIG)],
  ['أوامر البيع', () => firstOf([
    () => api.get('/sellgo_so', LIST),
    () => api.get('/sales/orders', LIST),
  ])],
  ['المرتجعات', () => api.get('/sellgo_v5_returns', LIST)],
  ['التحصيلات', () => firstOf([
    () => api.get('/sellgo_coll_history', LIST),
    () => api.get('/collections/payments', LIST),
  ])],
  ['المتأخرات', () => firstOf([
    () => api.get('/sellgo_coll_outstanding'),
    () => api.get('/collections/outstanding'),
  ])],
  ['لوحة التحصيل', () => firstOf([
    () => api.get('/sellgo_coll_dashboard'),
    () => api.get('/collections/dashboard'),
  ])],
  ['الهدايا', () => api.get('/sellgo_v5_gifts', LIST)],
  ['الزيارات', () => api.get('/visits', LIST)],
];

// SellGo's own database: the collection screen lists the company's payment methods.
const SELLGO_STEPS = [
  ['وسائل الدفع', () => api.get('/sellgo_payment_journals')],
];

const countOf = (r) => (Array.isArray(r?.data) ? r.data.length : (r?.success ? 1 : 0));

export async function prepareOffline(onProgress) {
  const results = {};
  let ok = true;
  const steps = getBackend() === 'supabase' ? [...STEPS, ...SELLGO_STEPS] : STEPS;
  for (const [label, fn] of steps) {
    if (onProgress) onProgress(label);
    try {
      results[label] = countOf(await fn());
    } catch {
      results[label] = 0;
      ok = false;
    }
  }
  return { ok, results };
}
